import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Pulls the recipe component breakdown for a given menu item.
//
// Joins items (menu item) → recipes → recipe_components → items
// (each ingredient). Returns line costs, totals, and the target food
// cost % off the recipe so the modal can show "current vs target".
//
// Same costing posture as useRecipesData / vw_menu_engineering (v1):
// quantity * component item's cost_per_unit. No cross-unit conversion.
export function useRecipeBreakdown(itemId) {
  const [state, setState] = useState({
    components: [],
    totalCost: 0,
    salePrice: 0,
    marginPct: null,
    targetFoodCostPct: null,
    loading: !!itemId,
    error: null,
  })

  useEffect(() => {
    if (!itemId) {
      setState({
        components: [], totalCost: 0, salePrice: 0,
        marginPct: null, targetFoodCostPct: null,
        loading: false, error: null,
      })
      return
    }

    let mounted = true
    setState((s) => ({ ...s, loading: true, error: null }))

    ;(async () => {
      try {
        const { data, error } = await supabase
          .from('items')
          .select(`
            id, name, sale_price,
            recipe:recipes!recipes_item_id_fkey(
              id, yield_servings, target_food_cost_pct, created_at,
              components:recipe_components(
                id, quantity, unit,
                component_item:item_id(id, name, unit, cost_per_unit)
              )
            )
          `)
          .eq('id', itemId)
          .maybeSingle()

        if (!mounted) return
        if (error) throw error

        // H2 — `recipes.item_id` has no UNIQUE constraint (drafts), so
        // an item can have multiple recipes. PostgREST returns them as
        // an array when there's more than one. Pick the most recent by
        // created_at to mirror the new vw_menu_engineering view dedupe
        // (most_recent_recipe CTE in 0049). If the field comes back as
        // a single object (one recipe), pass it through unchanged.
        // We deliberately do NOT use .maybeSingle() on the embedded
        // relationship — that's what threw before.
        let recipe = data?.recipe
        if (Array.isArray(recipe)) {
          const sorted = [...recipe].sort((a, b) => {
            const aT = a?.created_at ? Date.parse(a.created_at) : 0
            const bT = b?.created_at ? Date.parse(b.created_at) : 0
            return bT - aT
          })
          recipe = sorted[0] || null
        }
        const yieldN = Number(recipe?.yield_servings || 1)
        const rawComponents = recipe?.components || []

        const components = rawComponents.map((c) => {
          const qty = Number(c.quantity || 0)
          const unitCost = Number(c.component_item?.cost_per_unit || 0)
          const lineCost = qty * unitCost
          return {
            id: c.id,
            name: c.component_item?.name || '—',
            qty,
            unit: c.unit || c.component_item?.unit || '',
            unit_cost: unitCost,
            line_cost: lineCost,
          }
        })

        const totalCost = components.reduce((acc, c) => acc + c.line_cost, 0)
        const perServingCost = yieldN > 0 ? totalCost / yieldN : totalCost
        const salePrice = Number(data?.sale_price || 0)
        const marginPct = salePrice > 0
          ? Number((((salePrice - perServingCost) / salePrice) * 100).toFixed(1))
          : null

        setState({
          components,
          totalCost: perServingCost,
          salePrice,
          marginPct,
          targetFoodCostPct: recipe?.target_food_cost_pct != null
            ? Number(recipe.target_food_cost_pct)
            : null,
          loading: false,
          error: null,
        })
      } catch (e) {
        if (!mounted) return
        // eslint-disable-next-line no-console
        console.error('[useRecipeBreakdown] failed', e)
        setState({
          components: [], totalCost: 0, salePrice: 0,
          marginPct: null, targetFoodCostPct: null,
          loading: false,
          error: e?.message || 'Could not load recipe breakdown.',
        })
      }
    })()

    return () => { mounted = false }
  }, [itemId])

  return state
}
